import type { BatchFile } from 'services/TwinsBatchApiService'
import type { DigitalTwinsFile } from 'models/DigitalTwinsFile'
import { twinToPatch } from 'utils/twins'

/**
 * The runBatchPromises method executes an array of promises in batches of a specified size.
 * The results are returned in the same order as the input promises.
 * If any of the promises fail, the first error is thrown.
 * @param promises - An array of promises to be executed in batches.
 * @param batchSize - The size of each batch. Default is 7.
 * @returns A promise that resolves to an array of results in the same order as the input promises.
 * @throws The first error encountered during the execution of the promises.
 */
export const runBatchPromises = async <T>(promises: Promise<T>[], batchSize = 7): Promise<T[]> => {
  const _promises: Promise<void>[] = []
  const results: (T | undefined)[] = new Array(promises.length) // Preallocate array to maintain order
  const errors: unknown[] = []

  for (let i = 0; i < promises.length; i++) {
    const promise = promises[i]
    const wrappedPromise = promise
      .then((result) => {
        results[i] = result // Store result at the correct index
      })
      .catch((error) => {
        errors.push(error)
      })
      .finally(() => {
        // Remove the resolved/rejected promise from the _promises array
        const index = _promises.indexOf(wrappedPromise)
        if (index > -1) {
          _promises.splice(index, 1)
        }
      })

    _promises.push(wrappedPromise)

    // Wait for the batch to complete if the batch size is reached
    if (_promises.length >= batchSize) {
      await Promise.race(_promises)
    }
  }

  // Wait for all remaining promises to complete
  await Promise.all(_promises)

  // If there were errors, throw the first one (or handle them as needed)
  if (errors.length > 0) {
    throw errors[0]
  }

  return results as T[] // Cast to T[] since all indices should be filled
}

/**
 * Transform a standard DigitalTwinsFile to a BatchFile, taking into account settings to transform to patches
 * @param digitalTwinsFile
 * @param digitaltwinsOp
 * @param relationshipsOp
 */
export const digitalTwinsFileToBatchFile = (
  digitalTwinsFile: DigitalTwinsFile,
  digitaltwinsOp: 'patch' | 'put' = 'patch',
  relationshipsOp: 'put' = 'put'
): BatchFile => {
  const _batchFile: BatchFile = {}
  // DigitalTwins
  if (digitaltwinsOp === 'put' && digitalTwinsFile?.digitalTwinsGraph?.digitalTwins?.length) {
    if (!_batchFile.put) _batchFile.put = {}
    _batchFile.put.digitaltwins = digitalTwinsFile.digitalTwinsGraph?.digitalTwins
  }
  if (digitaltwinsOp === 'patch' && digitalTwinsFile?.digitalTwinsGraph?.digitalTwins?.length) {
    if (!_batchFile.patch) _batchFile.patch = {}
    _batchFile.patch.digitaltwins = digitalTwinsFile.digitalTwinsGraph?.digitalTwins.map((t) => ({
      twinId: t.$dtId,
      patch: twinToPatch(t, 'add')
    }))
  }
  // Relationships
  if (relationshipsOp === 'put' && digitalTwinsFile?.digitalTwinsGraph?.relationships?.length) {
    if (!_batchFile.put) _batchFile.put = {}
    _batchFile.put.relationships = digitalTwinsFile.digitalTwinsGraph?.relationships
  }
  // Models
  if (digitalTwinsFile.digitalTwinsModels?.length) {
    _batchFile.models = digitalTwinsFile.digitalTwinsModels
  }
  return _batchFile
}

/**
 * The mergeBatchFiles function takes in a variable number of arguments using the rest parameter syntax (...batchFiles: BatchFile[]). It then initializes an empty result object with the same structure as the BatchFile interface.
 */
export const mergeBatchFiles = (...batchFiles: (BatchFile | undefined)[]): BatchFile => {
  const result: BatchFile = {}

  batchFiles.forEach((batchFile) => {
    if (!batchFile) return
    if (batchFile.put) {
      if (!result.put) result.put = {}
      if (batchFile.put.digitaltwins) {
        result.put.digitaltwins = [
          ...(result.put.digitaltwins ?? []),
          ...batchFile.put.digitaltwins
        ]
      }
      if (batchFile.put.relationships) {
        result.put.relationships = [
          ...(result.put.relationships ?? []),
          ...batchFile.put.relationships
        ]
      }
    }

    if (batchFile.patch) {
      if (!result.patch) result.patch = {}
      if (batchFile.patch.digitaltwins) {
        result.patch.digitaltwins = [
          ...(result.patch.digitaltwins ?? []),
          ...batchFile.patch.digitaltwins
        ]
      }
      if (batchFile.patch.relationships) {
        result.patch.relationships = [
          ...(result.patch.relationships ?? []),
          ...batchFile.patch.relationships
        ]
      }
    }

    if (batchFile.delete) {
      if (!result.delete) result.delete = {}
      if (batchFile.delete.digitaltwins) {
        result.delete.digitaltwins = [
          ...(result.delete.digitaltwins ?? []),
          ...batchFile.delete.digitaltwins
        ]
      }
      if (batchFile.delete.relationships) {
        result.delete.relationships = [
          ...(result.delete.relationships ?? []),
          ...batchFile.delete.relationships
        ]
      }
    }

    if (batchFile.models) {
      if (!result.models) result.models = []
      result.models = [...result.models, ...batchFile.models]
    }
  })

  return result
}
